import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Caption, CheckBoxCard, Notify, SubHeadingOne } from 'components/core';

import { useRecoilState } from 'recoil';
import {
  currentVisualizationState,
  deleteVisualisationDashboardsState,
  insertVisualisationDashboardsState,
  visualisationDashboardsState,
  currentMenu,
} from 'states';
import { allDashboards } from 'states/atoms';
import { useDashboardService } from 'services';
import styles from './VisualizationTabs.module.css';

interface DashboardsProps {
  register: any;
  setValue: any;
  formState: any;
  saveAttempted?: boolean;
}

export const Dashboards = ({ saveAttempted = false }: DashboardsProps) => {
  const { t } = useTranslation();
  const [dashboards, setDashboards] = useRecoilState(allDashboards);
  const [currentVisualization] = useRecoilState(currentVisualizationState);
  const [visualisationDashboards, setVisualisationDashboards] = useRecoilState(
    visualisationDashboardsState
  );
  const dashboardService = useDashboardService();
  const [initialVisualisationDashboards, setInitialVisualisationDashboards] = useState<any[]>([]);
  const [, setDeleteVisualisationDashboards] = useRecoilState(deleteVisualisationDashboardsState);
  const [, setInsertVisualisationDashboards] = useRecoilState(insertVisualisationDashboardsState);
  const [selectedMenu] = useRecoilState(currentMenu);

  const [removeList, setRemoveList] = useState<any[]>([]);

  const [insertList, setInsertList] = useState<any[]>([]);

  let insertDashboard: any;
  let removeDashboard: any;

  useEffect(() => {
    getDashboards();

    if (!currentVisualization?.data?.length) {
      setVisualisationDashboards([]);
      setInitialVisualisationDashboards([]);
      return;
    }

    const dashboardsData = currentVisualization.data[0]?.dashboards;
    const permissionData = currentVisualization.visualizationPermission;

    let parsedDashboards: any[] = [];

    const tryParse = (input: any) => {
      try {
        let temp = input;
        while (typeof temp === 'string') {
          temp = JSON.parse(temp);
        }
        return Array.isArray(temp) ? temp : [];
      } catch {
        return [];
      }
    };

    parsedDashboards = tryParse(dashboardsData);

    if (!parsedDashboards.length) {
      parsedDashboards = tryParse(permissionData);
    }

    const formatted = parsedDashboards.map((id: string) => ({
      dashboardId: id,
    }));

    setVisualisationDashboards(formatted);
    setInitialVisualisationDashboards(formatted);
  }, [currentVisualization]);

  useEffect(() => {
    setRemoveList([]);
    setInsertList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVisualization, selectedMenu]);

  useEffect(() => {
    removeDashboard = {
      dashboards: removeList,
      visualizationCode: currentVisualization.visualizationCode,
    };

    insertDashboard = {
      dashboards: insertList,
      visualizationCode: currentVisualization.visualizationCode,
    };

    setDeleteVisualisationDashboards(removeDashboard);
    setInsertVisualisationDashboards(insertDashboard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeList, insertList, visualisationDashboards]);

  const getDashboards = () => {
    dashboardService.getDashboards().then(
      (response: any) => {
        if (response.length > 0) {
          setDashboards(response);
        }
      },
      (error) => {
        Notify.error(error);
      }
    );
  };

  const getDashboardClickHandler = (dashboard: any) => {
    return () => {
      updateDashboards(dashboard);
    };
  };

  const isDashboardSelected = (dashboardId: string) => {
    const currentVisualisationDashboards: any[] = [...visualisationDashboards];
    return (
      currentVisualisationDashboards.findIndex(
        (dashboard) => dashboard.dashboardId === dashboardId
      ) !== -1
    );
  };

  const toggleDashboardInList = (currentList: any[], selectedDashboard: any, dashboardId: string) => {
    const index = currentList.findIndex((d) => d.dashboardId === dashboardId);
    return index === -1
      ? [...currentList, selectedDashboard]
      : currentList.filter((_, i) => i !== index);
  };

  const toggleIdInList = (list: string[], dashboardId: string) =>
    list.includes(dashboardId) ? list.filter((id) => id !== dashboardId) : [...list, dashboardId];

  const handleNoInitialDashboards = (dashboardId: string) => {
    setInsertList((prev) => toggleIdInList(prev, dashboardId));
  };

  const handleDashboardInInitial = (dashboardId: string, status: boolean, existsInInitial: boolean) => {
    if (!(status && existsInInitial)) {
      setRemoveList((prev) => prev.filter((id) => id !== dashboardId));
      return;
    }

    if (!removeList.includes(dashboardId)) {
      setRemoveList((prev) => [...prev, dashboardId]);
    }
  };

  const handleDashboardNotInInitial = (dashboardId: string) => {
    setInsertList((prev) =>
      prev.includes(dashboardId) || removeList.includes(dashboardId)
        ? prev.filter((id) => id !== dashboardId)
        : [...prev, dashboardId]
    );
  };

  const updateDashboards = (selectedDashboard: any) => {
    const dashboardId = selectedDashboard.dashboardId;
    const visualizationCode = currentVisualization.visualizationCode;

    const currentList = toggleDashboardInList([...visualisationDashboards], selectedDashboard, dashboardId);
    setVisualisationDashboards(currentList);

    const existsInInitial = initialVisualisationDashboards?.some(
      (i: any) => i.dashboardId === dashboardId
    );
    const status = isDashboardSelected(dashboardId);

    // Case 1: No initial dashboards
    if (!initialVisualisationDashboards?.length) {
      handleNoInitialDashboards(dashboardId);
      return;
    }

    const match = initialVisualisationDashboards.find(
      (i: any) => i.dashboardId === dashboardId && i.visualizationCode === visualizationCode
    );

    // Case 2: Exists in initial → remove logic
    if (match) {
      handleDashboardInInitial(dashboardId, status, existsInInitial);
      return;
    }

    // Case 3: Not in initial → insert logic
    handleDashboardNotInInitial(dashboardId);
  };
  return (
    <>
      <div className="col-12 mb-3">
        <div className="mt-4">
          <SubHeadingOne title="Dashboards" />
        </div>
        <div className="">
          <Caption text="This visualization is available on the following dashboards." />
        </div>

        {/* Passive hint — no dashboard selected, before save attempted */}
        {visualisationDashboards.length === 0 && !saveAttempted && (
          <div className={`d-flex align-items-center gap-2 mt-3 mb-3 ${styles.accessHint}`} role="note">
            <span className={`material-icons ${styles.accessHintIcon}`}>info</span>
            <span>{t('VALIDATION.VISUALIZATION_DASHBOARDS_HINT')}</span>
          </div>
        )}

        {/* Hard error — after failed save with no dashboards selected */}
        {saveAttempted && visualisationDashboards.length === 0 && (
          <div className={`d-flex align-items-center gap-2 mt-3 mb-3 ${styles.accessError}`} role="alert">
            <span className={`material-icons ${styles.accessErrorIcon}`}>error_outline</span>
            <span>{t('VALIDATION.VISUALIZATION_DASHBOARDS_REQUIRED')}</span>
          </div>
        )}

        <div className="row">
          {dashboards.length > 0 &&
            dashboards.map((dashboard: any) => {
              return (
                <div className="col-sm-12 col-md-4 col-lg-4 mt-3" key={dashboard.dashboardId}>
                  <CheckBoxCard
                    label={dashboard.title!}
                    clickHandler={getDashboardClickHandler(dashboard)}
                    isChecked={isDashboardSelected(dashboard.dashboardId!)}
                  />
                </div>
              );
            })}
          <div className=""></div>
          <div>
            <Caption text="Unselect to remove" />
          </div>
        </div>
      </div>
    </>
  );
};
